import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, EmptyState, Header } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { WorkspaceHubBack } from "@/components/workspace/WorkspaceHub";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import { ImageUploadField } from "@/components/ImageUploadField";
import { SmartMatchChip } from "@/components/media/SmartMatchChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGetJson, apiSendJson, clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { Download, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/marcas")({
  component: MarcasPage,
  head: () => ({ meta: [{ title: "Marcas — Catalogación" }] }),
});

interface Brand {
  id: string;
  name: string;
  slug: string;
  imageUrl?: string | null;
}

function apiErrorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

function MarcasPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [formName, setFormName] = useState("");
  const [formImageUrl, setFormImageUrl] = useState("");

  const load = useCallback(() => {
    if (!tid) return;
    void apiGetJson<Brand[]>("/api/brands", tid)
      .then(setBrands)
      .catch((e) => toast.error(apiErrorMessage(e, "No se pudieron cargar las marcas")));
  }, [tid]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormName("");
    setFormImageUrl("");
    setEditorOpen(true);
  };

  const openEdit = (b: Brand) => {
    setEditing(b);
    setFormName(b.name);
    setFormImageUrl(b.imageUrl ?? "");
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!tid) {
      toast.error("No hay tenant activo. Si sos admin, elegí un tenant desde el panel.");
      return;
    }
    const name = formName.trim();
    if (!name) {
      toast.error("Escribí el nombre de la marca");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await apiSendJson(
          `/api/brands?id=${editing.id}`,
          "PATCH",
          { name, imageUrl: formImageUrl.trim() || null },
          tid,
        );
        toast.success("Marca actualizada");
      } else {
        const created = await apiSendJson<Brand>("/api/brands", "POST", { name }, tid);
        if (formImageUrl.trim()) {
          await apiSendJson(
            `/api/brands?id=${created.id}`,
            "PATCH",
            { imageUrl: formImageUrl.trim() },
            tid,
          );
        }
        toast.success("Marca creada");
      }
      setEditorOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "No se pudo guardar"));
    } finally {
      setSaving(false);
    }
  };

  const deleteBrand = async (id: string) => {
    if (!tid) return;
    try {
      await apiSendJson(`/api/brands?id=${id}`, "DELETE", {}, tid);
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const downloadCsv = async (path: string, filename: string) => {
    if (!tid) return;
    const res = await fetch(path, { credentials: "include", headers: clientRequestHeaders(tid) });
    if (!res.ok) {
      toast.error("Error al descargar");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: DataTableColumn<Brand>[] = [
    {
      key: "brand",
      header: "Marca",
      render: (b) => (
        <div className="flex min-w-0 items-center gap-3">
          {b.imageUrl ? (
            <img src={b.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded-md bg-muted/40" />
          )}
          <span className="min-w-0 truncate">
            {b.name} <span className="font-mono text-xs text-muted-foreground">({b.slug})</span>
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-24 text-right",
      render: (b) => (
        <div className="flex justify-end gap-1">
          <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(b)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={() => void deleteBrand(b.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header
        title="Marcas"
        actions={
          <Button type="button" className="gap-1.5" disabled={!tid} onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Nueva marca
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="pt-6">
        <WorkspaceHubBack
          title="Catalogación"
          onBack={() => void navigate({ to: "/app/catalogacion" })}
        />
        {!tid ? (
          <p className="mt-4 text-sm text-amber-600 dark:text-amber-400">
            Sin tenant activo no podés crear marcas.
          </p>
        ) : null}

        <div className="mt-4 space-y-4">
          <CrudEditorShell
            open={editorOpen}
            onOpenChange={(open) => {
              setEditorOpen(open);
              if (!open) setEditing(null);
            }}
            title={editing ? "Editar marca" : "Nueva marca"}
            size="md"
            list={
              <DataTable
                columns={columns}
                rows={brands}
                getRowKey={(b) => b.id}
                onRowClick={(b) => openEdit(b)}
                emptyState={
                  <EmptyState
                    title="Todavía no hay marcas"
                    description="Creá la primera marca para organizar tu catálogo."
                    action={
                      <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openCreate}>
                        <Plus className="h-4 w-4" /> Crear primera marca
                      </Button>
                    }
                  />
                }
              />
            }
            footer={
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={saving}
                  onClick={() => {
                    setEditorOpen(false);
                    setEditing(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button type="button" className="flex-1" disabled={saving} onClick={() => void handleSave()}>
                  {editing ? "Guardar cambios" : "Crear marca"}
                </Button>
              </>
            }
          >
            <div className="space-y-4">
              <div className="grid gap-1.5">
                <FieldLabel htmlFor="brand-name" tipKey="brand.name" required>
                  Nombre
                </FieldLabel>
                <Input
                  id="brand-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Nombre de marca"
                  disabled={saving}
                  autoFocus
                />
              </div>
              <div className="grid gap-1.5">
                <FieldLabel tipKey="brand.image">Imagen</FieldLabel>
                {editing && !formImageUrl.trim() && tid ? (
                  <SmartMatchChip
                    tenantId={tid}
                    entityType="brand"
                    entityId={editing.id}
                    slug={editing.slug}
                    name={formName || editing.name}
                    onLinked={(url) => setFormImageUrl(url)}
                  />
                ) : null}
                <ImageUploadField
                  kind="brand"
                  value={formImageUrl.trim() || null}
                  onChange={(url) => setFormImageUrl(url ?? "")}
                  tenantId={tid}
                  disabled={saving}
                  libraryLink={editing ? { entityType: "brand", entityId: editing.id } : undefined}
                />
              </div>
            </div>
          </CrudEditorShell>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => void downloadCsv("/api/brands/export", "marcas.csv")}
          >
            <Download className="h-4 w-4" /> Exportar marcas (CSV con id)
          </Button>
        </div>
      </ClinicalPageShell>
    </div>
  );
}
